import { z } from 'zod';
import { isoDateSchema } from './common.js';
import { projectStatusSchema } from './project.js';

/**
 * Wave 17 — Carteira (Portfolio).
 *
 * A single endpoint that aggregates across every project the caller has
 * access to, so the home dashboard doesn't have to fan out N per-project
 * queries. The response is intentionally shallow — we don't need bucketed
 * series here, just the "how's the whole business doing" strip + the
 * project cards, sorted so the at-risk ones surface first.
 *
 * Access scoping matches project_members: admins see everything,
 * everyone else sees projects where they hold any role. Cost/spend
 * numbers are only filled in for crew roles — client/broker-only viewers
 * get the non-financial fields and zeros for the rest (the web hides
 * the cost KPIs in that case).
 */

export const portfolioProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  clientName: z.string(),
  status: projectStatusSchema,
  startDate: isoDateSchema.nullable(),
  estimatedEndDate: isoDateSchema.nullable(),
  /** 0..100, averaged from work_stages.progress_percent (same as listProjects). */
  progressPercent: z.number().min(0).max(100),
  /** projects.budget_cents or null. */
  budgetCents: z.number().int().nonnegative().nullable(),
  /** Sum of non-cancelled PO total_cents (0 when caller is client/broker-only). */
  committedCents: z.number().int().nonnegative(),
  /** Sum of settled payment outflows (0 when caller is client/broker-only). */
  paidCents: z.number().int().nonnegative(),
  /** Count of open issues with severity high/blocker on this project. */
  openIssuesCount: z.number().int().nonnegative(),
  /** Count of open measurements awaiting approval (status='sent'). */
  pendingMeasurementsCount: z.number().int().nonnegative(),
  /**
   * Computed flag: project is behind schedule or over budget.
   *
   * Drivers (any one is enough):
   *   - estimatedEndDate is in the past AND status not in (completed, archived)
   *   - committedCents > budgetCents (when budget is set)
   *   - progress below the straight-line planned progress by >= 15 points
   *     (only when we have both startDate and estimatedEndDate)
   */
  atRisk: z.boolean(),
});
export type PortfolioProject = z.infer<typeof portfolioProjectSchema>;

export const portfolioTotalsSchema = z.object({
  projectsCount: z.number().int().nonnegative(),
  activeCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  atRiskCount: z.number().int().nonnegative(),
  /** Average progressPercent across active projects (0..100). */
  avgProgressPercent: z.number().min(0).max(100),
  /** Sum of budget_cents across all accessible projects (nulls treated as 0). */
  budgetCents: z.number().int().nonnegative(),
  /** Sum of PO commitments across accessible projects. */
  committedCents: z.number().int().nonnegative(),
  /** Sum of settled payment outflows across accessible projects. */
  paidCents: z.number().int().nonnegative(),
  /** Sum of open (unsettled) measurement receivables — recognized revenue not yet collected. */
  receivablesCents: z.number().int().nonnegative(),
  /** Total open high/blocker issues across all accessible projects. */
  openIssuesCount: z.number().int().nonnegative(),
  /** Total measurements in `sent` status across accessible projects. */
  pendingMeasurementsCount: z.number().int().nonnegative(),
});
export type PortfolioTotals = z.infer<typeof portfolioTotalsSchema>;

export const portfolioSummarySchema = z.object({
  totals: portfolioTotalsSchema,
  projects: z.array(portfolioProjectSchema),
  /** True when the caller is client/broker-only on every accessible project. */
  financialsRedacted: z.boolean(),
  generatedAt: z.string(),
});
export type PortfolioSummary = z.infer<typeof portfolioSummarySchema>;
